import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
    ServiceWizard,
    ServiceWizardCheckedEvent,
} from '../../../state/service-wizard/service-wizard.model';
import { ServiceWizardQuery } from '../../../state/service-wizard/service-wizard.query';
import { FamilyFolderModel } from '../../../models/family-folder.model';
import { PackWizard } from 'src/app/shared/state/pack-wizard/pack-wizard.model';
import { ModalController } from '@ionic/angular';
import { AppRolesModel, GenericKeysModel } from '../../../models';
import { SettingsQuery } from '../../../state/settings/settings.query';
import { ServiceViewComponent } from '../../../../modules/appointment-wizard/components/service-view/service-view.component';
import {
    BreadcrumbItem,
    BreadcrumbNavigateEventDetail,
    FamilySelectorClickEventDetail,
    TabOption,
    TabsChangeEventDetail,
} from '@flowww/apps-design-system';
import {
    buildBreadcrumbTrail,
    computeDefaultFamilyPath,
    countItemsInFamily,
    mapFamilyTab,
    resolveFamilyByPath,
} from '../../../../core/utils/family-tabs.util';
import {
    sheetModalEnterAnimation,
    sheetModalLeaveAnimation,
} from '../../../animations/sheet-modal.animation';

const OTHERS_TAB_VALUE = '0';

@Component({
    standalone: false,
    selector: 'app-family-service-list',
    templateUrl: './family-service-list.component.html',
    styleUrls: ['./family-service-list.component.scss'],
})
export class FamilyServiceListComponent implements OnInit, OnChanges {
    @Input() families: FamilyFolderModel[];
    @Input() isLoadingContent: boolean;
    @Input() servicesWizard: ServiceWizard[];
    @Input() noMoreServices: boolean;
    @Input() appDelegate: boolean;
    @Input() packs: PackWizard[] = [];
    @Input() activeIndex: any;
    @Input() searchText: string = '';

    @Output() serviceClickedEvent = new EventEmitter<
        ServiceWizardCheckedEvent
    >();
    @Output() serviceToggleEvent = new EventEmitter<any>();
    @Output() changeServiceSearchbarEvent: EventEmitter<any> = new EventEmitter<
        any
    >();
    @Output() packToggleEvent = new EventEmitter<any>();

    familyTabs: TabOption[] = [];
    activeFamilyTab: string;
    activeFamilyPath: string[] = [];

    /** Cache of live per-service `checked` observables, keyed by service id. */
    private checkedObservables = new Map<string, Observable<boolean>>();

    constructor(
        private serviceWizardQuery: ServiceWizardQuery,
        private modalController: ModalController,
        private settingsQuery: SettingsQuery,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.families || changes.servicesWizard || changes.packs) {
            this.buildFamilyTabs();
        }
    }

    buildFamilyTabs(): void {
        const tabs: TabOption[] = (this.families ?? []).map((family) => mapFamilyTab(family));

        if (this.servicesWizard?.length > 0 || this.packs?.length > 0) {
            tabs.push({
                label: this.translateService.instant('lbl_others'),
                value: OTHERS_TAB_VALUE,
            });
        }

        this.familyTabs = tabs;

        if (!tabs.some((tab) => tab.value === this.activeFamilyTab)) {
            this.activeFamilyTab = tabs[0]?.value;
            this.activeFamilyPath = this.activeFamilyTab
                ? computeDefaultFamilyPath(this.activeFamilyTab)
                : [];
        }
    }

    get showFamilyTabs(): boolean {
        return this.families?.length > 0;
    }

    /** The family folder the user has drilled down into */
    get currentFamilyFolder(): FamilyFolderModel | undefined {
        return resolveFamilyByPath(this.families, this.activeFamilyPath);
    }

    /** Subfamilies of the current folder, rendered as `flw-family-selector` */
    get currentSubfamilies(): FamilyFolderModel[] {
        if (this.activeFamilyTab === OTHERS_TAB_VALUE) {
            return [];
        }
        return this.currentFamilyFolder?.families ?? [];
    }

    /** Path above the root tab, to feed `flw-breadcrumb` */
    get breadcrumbItems(): BreadcrumbItem[] {
        return buildBreadcrumbTrail(this.families, this.activeFamilyPath);
    }

    get visibleServices(): ServiceWizard[] {
        if (this.activeFamilyTab === OTHERS_TAB_VALUE) {
            return this.servicesWizard ?? [];
        }
        return this.currentFamilyFolder?.services ?? [];
    }

    get visiblePacks(): PackWizard[] {
        if (this.activeFamilyTab === OTHERS_TAB_VALUE) {
            return this.packs ?? [];
        }
        return this.currentFamilyFolder?.packs ?? [];
    }

    onFamilyTabChangeFilter(event: CustomEvent<TabsChangeEventDetail>) {
        this.activeFamilyTab = event.detail.value;
        this.activeFamilyPath = computeDefaultFamilyPath(event.detail.value);
    }

    onFamilyDrillDown(event: CustomEvent<FamilySelectorClickEventDetail>) {
        this.activeFamilyPath = [...this.activeFamilyPath, event.detail.value];
    }

    onBreadcrumbNavigate(event: CustomEvent<BreadcrumbNavigateEventDetail>) {
        this.activeFamilyPath = this.activeFamilyPath.slice(0, event.detail.index + 1);
    }

    countItems(family: FamilyFolderModel): number {
        return countItemsInFamily(family);
    }

    /**
     * `servicesWizard` can be a stale snapshot (e.g. taken when the family
     * was clicked), so `service.checked` alone isn't reliable — read the
     * live value from the store instead, same as the old `app-service-item`
     * did via `serviceIsChecked$`.
     */
    serviceChecked$(serviceId: string): Observable<boolean> {
        if (!this.checkedObservables.has(serviceId)) {
            this.checkedObservables.set(
                serviceId,
                this.serviceWizardQuery.selectEntity(serviceId, 'checked')
            );
        }
        return this.checkedObservables.get(serviceId);
    }

    onServiceClicked(serviceAndCheckedEvent: ServiceWizardCheckedEvent) {
        this.serviceClickedEvent.emit(serviceAndCheckedEvent);
    }

    /**
     * Forwards the event's own `checked` value rather than just the service:
     * `flw-service-selector` re-emits `flwChange` whenever its `checked` prop
     * changes for *any* reason, including when it's pushed externally (e.g.
     * a pack that's linked to this service toggling it). A handler that
     * blindly flips on every event turns that echo into an infinite on/off
     * loop; setting to the event's own value is idempotent.
     */
    onServiceToggle(service: ServiceWizard, event: CustomEvent<{ checked: boolean }>) {
        this.serviceToggleEvent.emit({ service, checked: event.detail.checked });
    }

    onPackToggle(event: { pack: PackWizard; checked: boolean }) {
        this.packToggleEvent.emit(event);
    }

    finalPrice(service: ServiceWizard) {
        if (service.price && service.price !== '') {
            if (service.promotedPrice && service.promotedPrice !== '') {
                return service.promotedPrice;
            }
            return service.price;
        }
    }

    get showModalService() {
        if (
            this.settingsQuery.appRole === AppRolesModel.Personalized ||
            this.settingsQuery.appRole === AppRolesModel.PersonalizedBrowser ||
            ((this.settingsQuery.appRole === AppRolesModel.Generic ||
                this.settingsQuery.appRole === AppRolesModel.GenericBrowser) &&
                this.settingsQuery.genericKey === GenericKeysModel.LPG)
        ) {
            return true;
        }
        return false;
    }

    openServiceInfoModal(service: ServiceWizard, _event: CustomEvent<{ value: string }>) {
        this.modalController.create({
            component: ServiceViewComponent,
            cssClass: this.settingsQuery.browser ? 'modal-flowwwer modal-browser' : 'modal-flowwwer',
            enterAnimation: sheetModalEnterAnimation,
            leaveAnimation: sheetModalLeaveAnimation,
            componentProps: {
                serviceId: service.id,
            },
        }).then(modal => modal.present());
    }

    /** Avoids tearing down/recreating rows on every store tick during a pack cascade. */
    trackByServiceId(_index: number, service: ServiceWizard): string {
        return service.id;
    }

    trackByPackId(_index: number, pack: PackWizard): string {
        return pack.id;
    }

    trackByFamilyId(_index: number, family: FamilyFolderModel): string {
        return family.id;
    }
}
